import type * as pf from '@amirada/lib-shared'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { ExtCmdType } from '@amirada/lib-wilo-shared'
import type { ECarts } from '@base/utils/constants'
import { useCartStore } from '@base/stores/cartStore'
import { def_newProjectUrlId } from '~/utils/constants'
import type { IOpenProject, IStoredProject } from '~/utils/types'
import { usePfinderAPI } from '~/composables/pfinderAPI'
import { usePriceListCart } from '~/composables/priceListCart'
import { ut_wilo_projectIdFromUrl, ut_wilo_setProjectUrl } from '~/utils/priceList'

/*
 * Opening a price-list project — the counterpart to `projectAutoSave`, which writes it.
 *
 * There are two ways in, and both are run by `usePriceListProject` (on the cart page): the click in the project management
 * (`openSaved`) and the url of a project called up directly (a bookmark, a reload). Both go through
 * the same function, so the two cannot drift. ⚑ Not from `CartManage.vue`, where the loading used to
 * sit: the management can be unmounted while a load is still out (15.09.2026), and what it would
 * open afterwards reaches nobody.
 *
 * ⚑ `loadById` fills the cart before the caller has the project in hand, so the caller must have let
 * go of any project still open first (`usePriceListProject#releaseProject`) — otherwise auto-save could write
 * this cart into that one.
 *
 * The shape on the server is described by `IStoredProject`; see the ai_memory entry
 * `reference_project_storage_shape` for why an older project is a bare array.
 */
export function useProjectOpen(cart: ECarts) {
  const cartStore = useCartStore()
  const pfinderAPI = usePfinderAPI()
  const priceList = usePriceListCart(cart)

  /*
   * Read one project and put it into the cart. Returns the project to open, or undefined when
   * nothing came back — deleted meanwhile, or belonging to another account. `fetchData` never
   * throws, it answers `undefined`, so this is the only signal there is.
   */
  async function loadById(uuid: string): Promise<IOpenProject | undefined> {
    const args: pfWilo.ExtCmd<ExtCmdType.CART_READ>['arg'] = { uuid }
    const res = await pfinderAPI.fetchData(ExtCmdType.CART_READ, args, {})
    if (!res || !res.result) { return undefined }

    let items: pf.CartItem[]
    let pdf: IOpenProject['pdf']
    try {
      const parsed: unknown = JSON.parse(res.result.data || '[]')
      if (Array.isArray(parsed)) {
        items = parsed
      }
      else {
        const stored = parsed as IStoredProject
        items = stored?.items || []
        pdf = stored?.pdf
      }
    }
    catch (err) {
      console.error('Error loading project:', (err as Error).message, uuid)
      return undefined
    }

    cartStore.setCart(cart, items)
    /*
     * Refreshes the real items against the current country and language; local markers and every
     * userDefined field travel through it untouched (cartStore.updateCartItemsInfo).
     * ⚑ A series whose path no longer resolves is dropped here, silently — an old project opened in
     * a country that has since lost a series comes back shorter.
     */
    await cartStore.setUpdateCart(cart)
    // A project saved before the row ids existed (24.09.2026) gets them now; a newer one has them.
    priceList.ensureRowIds()

    // A saved project exists on the server, so it is editable right away.
    return { id: uuid, name: res.result.name || '', editable: true, pdf }
  }

  /*
   * What an incoming url asks for. Three cases, and two of them end in the project management:
   *
   *   a uuid                  load it; if nothing comes back, fall back and clean the address
   *   the placeholder 'new'   a locked template that was never saved — there is nothing to load
   *   nothing                 the ordinary call, the management is the page
   *
   * Cleaning is a replace, so the fallback does not put a step in front of the back button.
   */
  async function openFromUrl(): Promise<IOpenProject | undefined> {
    const id: string = ut_wilo_projectIdFromUrl()
    if (!id) { return undefined }

    if (id !== def_newProjectUrlId) {
      const project: IOpenProject | undefined = await loadById(id)
      if (project) { return project }
    }
    ut_wilo_setProjectUrl('', false)
    return undefined
  }

  // Opening one from the management: the address gets a history entry, and that entry is what the
  // browser's back button follows to get here again.
  function show(project: IOpenProject): void {
    ut_wilo_setProjectUrl(project.id || def_newProjectUrlId, true)
  }

  return { loadById, openFromUrl, show }
}
